import React, { useEffect, useMemo, useState } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { addDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db, auth } from "../../firebase";
import {
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Eye,
  Users,
  CheckSquare,
  Clock,
  CircleCheck,
  Download,
} from "lucide-react";
import featureGatingService from "../../services/featureGatingService";
import { toast } from "react-toastify";
import { updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { ncltPost, ncltGet, NCLT_API_BASE_URL } from "../../services/ncltApi";
import { buildNcltParties } from "../../utils/ncltParties";
import { STATUS_PILL_BASE, statusPillSoft } from "../../styles/statusPill";




export default function NcltCaseDetails() {
  const API_BASE_URL = NCLT_API_BASE_URL;
  const navigate = useNavigate();
  const { state } = useLocation();
  const [exportProgress, setExportProgress] = useState({
  total: 0,
  exported: 0
});



  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(state?.page || 1);
  const [totalPages , setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // 🔹 SELECT / EXPORT STATE
  const [selectedCases, setSelectedCases] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const getCaseKey = (row) => row.filingNumber || row.caseNo;

  // Client-side search across the NCLT list fields (diary/filing no, case no,
  // parties, filing date, status)
  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const fields = [r.filingNumber, r.caseNo, r.parties, r.filingDate, r.statusText];
      return fields.some((f) => f != null && String(f).toLowerCase().includes(q));
    });
  }, [rows, searchQuery]);

  // Summary counts for the stat cards — derived from statusText already on each row.
  const statusCounts = useMemo(() => {
    const disposed = rows.filter((r) => (r.statusText || "").toLowerCase().includes("dispos")).length;
    const pending = rows.filter((r) => (r.statusText || "").toLowerCase().includes("pend")).length;
    return { total: rows.length, pending, disposed };
  }, [rows]);

useEffect(() => {
  if (!state?.data || !Array.isArray(state.data)) return;

  setRows(
    state.data.map((item) => ({
      filingNumber: item.filingNo || item.filingNumber || item.filing_number || "",
      caseNo: item.caseNo || item.caseNumber || item.case_number || "",
      parties: item.caseTitle1 && item.caseTitle2
        ? `${item.caseTitle1} VS ${item.caseTitle2}`
        : item.caseTitle1 || item.petitionerVsRespondent || item.parties || "",
      filingDate: item.filingDate || item.filing_date || "",
      statusText: item.status || item.caseStatus || item.statusText || item.caseStage || "",
      caseStage: item.caseStage || "",
    }))
  );

  setPage(state.page || 1);
  setHasNext(Boolean(state.hasNext));

  if (state.totalPages) {
    setTotalPages(state.totalPages);
  }
}, [state]);



const getSearchEndpoint = () => {
  const type = state?.searchType;
  switch (type) {
    case "Case Number": return "/case-number";
    case "Party Name": return "/party";
    case "Advocate Name": return "/advocate-name";
    default: return "/party";
  }
};

const loadPage = async (targetPage) => {
  if (targetPage < 1 || targetPage === page) return;

  setLoadingMore(true);

  try {
    const res = await ncltPost(getSearchEndpoint(), {
      ...state.searchParams,
      page: targetPage,
    });

    const json = res.body;

    const items = json.results || json.data || json;
    setRows(
      (Array.isArray(items) ? items : []).map((item) => ({
        filingNumber: item.filingNo || item.filingNumber || item.filing_number || "",
        caseNo: item.caseNo || item.caseNumber || item.case_number || "",
        parties: item.caseTitle1 && item.caseTitle2
          ? `${item.caseTitle1} VS ${item.caseTitle2}`
          : item.caseTitle1 || item.petitionerVsRespondent || item.parties || "",
        filingDate: item.filingDate || item.filing_date || "",
        statusText: item.status || item.caseStatus || item.statusText || item.caseStage || "",
        caseStage: item.caseStage || "",
      }))
    );

    setPage(json.page || targetPage);
    setHasNext(json.hasNext || false);

  } finally {
    setLoadingMore(false);
  }
};


  // 🔹 SELECT HANDLERS (keyed by case identity, so selection survives filtering)
  const toggleCase = (row) => {
    const key = getCaseKey(row);
    const next = new Set(selectedCases);

    next.has(key) ? next.delete(key) : next.add(key);
    setSelectedCases(next);
  };

  const toggleSelectAll = () => {
    const next = new Set(selectedCases);
    const allSelected =
      filteredRows.length > 0 && filteredRows.every((r) => next.has(getCaseKey(r)));
    if (allSelected) {
      filteredRows.forEach((r) => next.delete(getCaseKey(r)));
    } else {
      filteredRows.forEach((r) => next.add(getCaseKey(r)));
    }
    setSelectedCases(next);
  };

  // Keep the "Select All" checkbox in sync as the filter/selection changes
  useEffect(() => {
    if (filteredRows.length === 0) {
      setSelectAll(false);
      return;
    }
    setSelectAll(filteredRows.every((r) => selectedCases.has(getCaseKey(r))));
  }, [filteredRows, selectedCases]);

  const handleSearchChange = (value) => setSearchQuery(value);
  const handleClearSearch = () => setSearchQuery("");
  const fetchFullNcltCaseDetails = async (filingNo) => {
  if (!filingNo) throw new Error("No filing number provided");

  const res = await ncltGet(`/filing/${filingNo}`);

  if (!res.ok) {
    throw new Error("Failed to fetch NCLT case details");
  }

  return res.body;
};


 const exportSelectedCases = async () => {
  if (selectedCases.size === 0) return;

  const user = auth.currentUser;
  if (!user) {
    toast.error("User not logged in");

    return;
  }

  const casesToExport = rows.filter((r) =>
      selectedCases.has(getCaseKey(r))
    );

    // Get current pending case count for subscription tracking
    const pendingQuery = query(
      collection(db, "pending"),
      where("owner", "==", user.uid)
    );
    const pendingSnapshot = await getDocs(pendingQuery);
    let currentPendingCount = pendingSnapshot.size;
  setExporting(true);



setExportProgress({
  total: casesToExport.length,
  exported: 0,
});




  try {
    

    for (const c of casesToExport) {
      // ✅ FETCH FULL DETAILS
      let fullDetails = {};
      try {
        if (c.filingNumber) {
          fullDetails = await fetchFullNcltCaseDetails(c.filingNumber);
        }
      } catch (err) {
        console.warn("Failed to fetch full NCLT details:", err);
      }

      // Check for duplicate
      const exportFilingNo = fullDetails?.header?.filingNo || c.filingNumber || "";
      if (exportFilingNo) {
        const pendingQ = query(collection(db, "pending"), where("filingNo", "==", exportFilingNo), where("owner", "==", user.uid));
        const disposedQ = query(collection(db, "disposed"), where("filingNo", "==", exportFilingNo), where("owner", "==", user.uid));
        const [pSnap, dSnap] = await Promise.all([getDocs(pendingQ), getDocs(disposedQ)]);
        if (!pSnap.empty || !dSnap.empty) {
          toast.warning(`Case ${exportFilingNo} already exported, skipping`);
          setExportProgress((p) => ({ ...p, exported: p.exported + 1 }));
          continue;
        }
      }

      // Extract parties from the response
      const partyName = fullDetails?.header?.partyName || c.parties || "";
      const [petitioner = "", respondent = ""] =
        partyName.split(/\s+VS\s+/i) || [];

      const caseYearMatch = (fullDetails?.header?.regNo || c.caseNo || "").match(/\/(\d{4})/);
      const caseYear = caseYearMatch ? caseYearMatch[1] : "";

      const caseStage = fullDetails?.currentDetails?.case_stage || c.statusText || "";
      const normalizedStatus = caseStage.toLowerCase().includes("disposed")
        ? "disposed"
        : "pending";

      // Get subscription metadata if adding to pending collection
      let subscriptionMetadata = null;
      if (normalizedStatus === "pending") {
        subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
          user.uid,
          currentPendingCount
        );
      }

      // Petitioners / respondents and their advocates from partyDetails by party_type.
      // P1 => main petitioner, P2/P3... => other petitioners; R1 => main respondent, R2/R3... => other respondents.
      const parties = buildNcltParties(fullDetails?.partyDetails, petitioner, respondent);

      // Build case history from proceedingDetails
      const proceedings = fullDetails?.proceedingDetails || [];
      const lastProceeding = proceedings[proceedings.length - 1];
      const normalizeDate = (d) => d ? d.replace(/-/g, '/') : "";
      const previousHearingDate = normalizeDate(lastProceeding?.listing_date);
      const nextHearingDate = normalizeDate(lastProceeding?.next_list_or_dispose_date);

      const caseHistory = proceedings.map((h) => ({
        causeListType: "NCLT",
        judge: "—",
        businessOnDate: h.order_upload_datetime ? h.order_upload_datetime.replace(/-/g, '/') : "—",
        hearingDate: h.listing_date ? h.listing_date.replace(/-/g, '/') : "—",
        purpose: h.listing_purpose || "Listing / Order",
      }));

      const regNo = fullDetails?.header?.regNo || c.caseNo || c.filingNumber;
      const caseDoc = {
        caseNumber: regNo,
        caseNo: regNo,
        filingNo: fullDetails?.header?.filingNo || c.filingNumber || "",
        diaryNo: fullDetails?.header?.filingNo || c.filingNumber || "",

        caseType: fullDetails?.currentDetails?.case_type || "NCLT",
        court: "NCLT",
        courtName: "NCLT",

        petitionerName: parties.petitionerName,
        respondentName: parties.respondentName,
        allPetitioners: parties.allPetitioners,
        allRespondents: parties.allRespondents,

        caseFiledDate: fullDetails?.currentDetails?.filing_date || c.filingDate || "",
        caseYear,
        previousHearingDate,
        nextHearingDate,

        status: normalizedStatus,
        caseHistory,

        petitionerAdvocate: parties.petitionerAdvocate,
        respondentAdvocate: parties.respondentAdvocate,
        allPetitionerAdvocates: parties.allPetitionerAdvocates,
        allRespondentAdvocates: parties.allRespondentAdvocates,

        owner: user.uid,
        team: [user.uid],
        createdAt: new Date(),
        timestamp: serverTimestamp(),

        rawNcltData: {
          listRow: c,
          fullDetails,
        },

        payload: {
          source: "NCLT",
          statusText: c.statusText,
          searchParams: state.searchParams,
          exportedAt: new Date().toISOString(),
        },

        ncltProceedingDetails: proceedings,
        ncltApplicationDetails: fullDetails?.applicationDetails || [],
        ncltMainCaseDetails: fullDetails?.parentCaseDetails || [],

        files: [],
        casenotes: "",
        caseFilesLink: "",
        ...(subscriptionMetadata && { subscriptionMetadata })
      };

      // Check case limit before proceeding
      const limitResult = await featureGatingService.checkCaseLimitBeforeExport(
        user.uid,
        1,
        normalizedStatus
      );

      if (!limitResult.canProceed) {
        toast.error(`Case limit reached! Your ${limitResult.planName} plan allows ${limitResult.maxLimit} ${normalizedStatus} cases. Skipping remaining cases.`);
        break;
      }

      const docRef = await addDoc(
        collection(db, normalizedStatus),
        caseDoc
      );

      const caseId = docRef.id;
      const caseFilesLink = `/portal/case-files/${caseId}?from=nclt`;

      await updateDoc(docRef, { caseFilesLink });

      // Create hearing reminder events (only if next hearing date is in the future)
      try {
        const parseHearingDate = (str) => {
          if (!str) return null;
          const iso = str.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
          if (iso) return new Date(parseInt(iso[1]), parseInt(iso[2]) - 1, parseInt(iso[3]));
          const dmy = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
          if (dmy) return new Date(parseInt(dmy[3]), parseInt(dmy[2]) - 1, parseInt(dmy[1]));
          return null;
        };
        const formatYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

        const eventDate = parseHearingDate(nextHearingDate);
        if (eventDate && !isNaN(eventDate.getTime())) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const hearingDay = new Date(eventDate);
          hearingDay.setHours(0, 0, 0, 0);

          if (hearingDay >= today) {
            const reminderDate = new Date(eventDate);
            reminderDate.setHours(8, 0, 0, 0);
            const eventTimeIST = new Date(eventDate);
            eventTimeIST.setHours(18, 0, 0, 0);
            const now = new Date();

            if (reminderDate > now) {
              const reminderEventData = {
                "Case No": regNo,
                date: formatYMD(reminderDate),
                id: Math.floor(Math.random() * 90000) + 10000,
                time: "08:00 AM",
                time1: reminderDate.getTime(),
                title: `${petitioner.trim() || "—"} VS ${respondent.trim() || "—"}`,
                description: `Hearing Reminder : Case No. - ${regNo}`,
                isScheduled: true,
                status: normalizedStatus,
                eventType: "reminder",
              };
              const reminderEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), reminderEventData);
              await addDoc(collection(db, "eventReminders"), {
                eventId: reminderEventRef.id,
                reminderTime: reminderDate,
                eventTitle: `Hearing Reminder : Case No. - ${regNo}`,
                caseNo: regNo,
                caseId: caseId,
                collection: normalizedStatus,
                status: "pending",
                createdAt: new Date(),
                scheduledBy: user.uid,
                recipientId: user.uid,
              });
            }

            if (eventTimeIST > now) {
              const mainEventData = {
                "Case No": regNo,
                date: formatYMD(eventDate),
                id: Math.floor(Math.random() * 90000) + 10000,
                time: "06:00 PM",
                time1: eventTimeIST.getTime(),
                title: `Update Next Hearing: ${petitioner.trim() || "—"} VS ${respondent.trim() || "—"}`,
                description: `Case No. - ${regNo}`,
                isScheduled: true,
                status: normalizedStatus,
                eventType: "updateReminder",
                isHidden: true,
              };
              const mainEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), mainEventData);
              await addDoc(collection(db, "eventReminders"), {
                eventId: mainEventRef.id,
                reminderTime: eventTimeIST,
                eventTitle: `Update Next Hearing : ${regNo}`,
                caseNo: regNo,
                caseId: caseId,
                collection: normalizedStatus,
                status: "pending",
                createdAt: new Date(),
                scheduledBy: user.uid,
                recipientId: user.uid,
              });
            }
          } else {
            console.log("NCLT: skipping event creation — next hearing date is in the past:", nextHearingDate);
          }
        }
      } catch (notificationError) {
        console.error("NCLT notification setup error:", notificationError);
      }

      // Save order PDFs from proceedingDetails
      if (Array.isArray(fullDetails?.proceedingDetails)) {
        const ordersWithPath = fullDetails.proceedingDetails.filter(h => h.encPath);
        if (ordersWithPath.length > 0) {
          const orderFolderRef = await addDoc(collection(db, "folders"), {
            name: "Orders",
            caseId,
            caseNo: caseDoc.caseNumber,
            lawyer_uid: user.uid,
            createdAt: new Date().toISOString(),
          });

          for (const h of ordersWithPath) {
            const pdfUrl = `${API_BASE_URL}/order?encPath=${encodeURIComponent(h.encPath)}`;
            const orderDate = h.listing_date || "Unknown Date";
            const orderName = h.order_name || "Order";

            await addDoc(collection(db, "files"), {
              caseNo: caseDoc.caseNumber,
              fileName: `${orderName} - ${orderDate}`,
              file_url: pdfUrl,
              lawyer_uid: user.uid,
              uploadedAt: h.order_upload_datetime || new Date().toISOString(),
              source: "NCLT",
              folderId: orderFolderRef.id,
            });
          }
        }
      }


      setExportProgress((p) => ({
  ...p,
  exported: p.exported + 1,
}));
// Increment pending count after adding a pending case
if (normalizedStatus === "pending") {
  currentPendingCount++;
}

    }

    alert(`Successfully exported ${casesToExport.length} NCLT cases`);

    setSelectedCases(new Set());
    setSelectAll(false);

  } catch (err) {
    console.error("NCLT export failed:", err);
    toast.error("Failed to export NCLT cases");

  } finally {
  setExporting(false);
  setExportProgress({ total: 0, exported: 0 });
}

};

const getPaginationPages = (current, total) => {
  const pages = [];

  if (total <= 6) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  pages.push(1);

  if (current > 3) pages.push("...");

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - 2) pages.push("...");

  pages.push(total);

  return pages;
};
useEffect(() => {
  console.log("PAGINATION STATE →", { page, totalPages, hasNext });
}, [page, totalPages, hasNext]);

// The new API returns all results at once, so pagination discovery is not needed.
// totalPages stays at 1 unless the API adds pagination in the future.



  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="mb-4 flex items-center gap-1.5 text-sm">
          <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">
            Find Cases
          </Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <Link to="/portal/fetchfromnclt" className="text-slate-500 hover:text-slate-800">
            NCLT
          </Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Results</span>
        </div>

        {/* Stepper */}
        <div className="mb-4">
          <FindCaseStepper current={4} />
        </div>

        {/* Summary stat cards */}
        {rows.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              { label: "Total Found", value: statusCounts.total, Icon: Users, tint: "bg-indigo-50 text-indigo-500" },
              { label: "Selected", value: selectedCases.size, Icon: CheckSquare, tint: "bg-blue-50 text-blue-500" },
              { label: "Pending", value: statusCounts.pending, Icon: Clock, tint: "bg-green-50 text-green-500" },
              { label: "Disposed", value: statusCounts.disposed, Icon: CircleCheck, tint: "bg-red-50 text-red-500" },
            ].map(({ label, value, Icon, tint }) => (
              <div
                key={label}
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold leading-tight text-slate-900">{value}</p>
                  <p className="truncate text-xs text-slate-500">{label}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Cases card */}
        <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {rows.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-base font-medium text-slate-700">No cases found</p>
              <p className="mt-1 text-sm text-slate-400">Try adjusting your search criteria</p>
            </div>
          ) : (
            <>
              {/* Toolbar — search + export */}
              <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full max-w-sm">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search in results..."
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                      title="Clear search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-3">
                  {selectedCases.size > 0 && (
                    <span className="text-sm text-slate-500">{selectedCases.size} selected</span>
                  )}
                  <button
                    onClick={exportSelectedCases}
                    disabled={exporting || selectedCases.size === 0}
                    className={`flex h-10 items-center gap-2 rounded-lg px-5 text-sm font-semibold transition-colors ${
                      exporting || selectedCases.size === 0
                        ? "cursor-not-allowed bg-slate-100 text-slate-400"
                        : "bg-yellow-500 text-slate-900 hover:bg-yellow-400"
                    }`}
                  >
                    <Download className="h-4 w-4" />
                    {exporting ? "Exporting..." : `Export Selected (${selectedCases.size})`}
                  </button>
                </div>
              </div>

              {filteredRows.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-base font-medium text-slate-700">No cases match your search</p>
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="mt-2 text-sm font-medium text-yellow-600 hover:underline"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table w-full border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <th className="w-12 px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={toggleSelectAll}
                            disabled={filteredRows.length === 0}
                            title="Select all"
                            className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                          />
                        </th>
                        <th className="px-4 py-3">Diary No</th>
                        <th className="px-4 py-3">Case No</th>
                        <th className="px-4 py-3">Petitioner Vs Respondent</th>
                        <th className="px-4 py-3">Filing Date</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Case Stage</th>
                        <th className="w-16 px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredRows.map((r) => {
                        const key = getCaseKey(r);
                        const openCase = () => {
                          if (!r.filingNumber) return;
                          navigate("/portal/ncltCaseFullDetails", {
                            state: { filingNumber: r.filingNumber },
                          });
                        };
                        return (
                          <tr
                            key={key}
                            className={`border-b border-slate-100 transition-colors duration-150 ${
                              selectedCases.has(key) ? "bg-yellow-50/60" : "hover:bg-slate-50"
                            }`}
                          >
                            <td className="px-4 py-3.5">
                              <input
                                type="checkbox"
                                checked={selectedCases.has(key)}
                                onChange={() => toggleCase(r)}
                                className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                              />
                            </td>
                            <td className="px-4 py-3.5 text-sm font-medium text-slate-900">
                              {r.filingNumber || "—"}
                            </td>
                            <td className="px-4 py-3.5 text-sm text-slate-600">{r.caseNo || "—"}</td>
                            <td className="px-4 py-3.5 text-sm text-slate-600">{r.parties || "—"}</td>
                            <td className="px-4 py-3.5 text-sm text-slate-600">{r.filingDate || "—"}</td>
                            <td className="px-4 py-3.5 text-sm text-slate-600">
                              {r.statusText ? (
                                <span className={`${STATUS_PILL_BASE} ${statusPillSoft(r.statusText)}`}>
                                  {r.statusText}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-4 py-3.5 text-sm text-slate-600">{r.caseStage || "—"}</td>
                            <td className="px-4 py-3.5 text-center">
                              <button
                                type="button"
                                onClick={openCase}
                                disabled={!r.filingNumber}
                                title="View case details"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Footer — result range + pagination */}
              {filteredRows.length > 0 && (
                <div className="flex flex-col items-center gap-4 border-t border-slate-100 px-4 py-4 sm:flex-row sm:justify-between">
                  <p className="text-sm text-slate-500">
                    Showing 1 to {filteredRows.length} of {filteredRows.length} results
                  </p>

                  {totalPages > 1 && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => loadPage(page - 1)}
                        disabled={page === 1}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>

                      {getPaginationPages(page, totalPages).map((p, idx) =>
                        p === "..." ? (
                          <span key={`ellipsis-${idx}`} className="px-1 text-slate-400">
                            ...
                          </span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => loadPage(p)}
                            className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition-colors ${
                              p === page
                                ? "bg-yellow-500 text-slate-900"
                                : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                            }`}
                          >
                            {p}
                          </button>
                        )
                      )}

                      <button
                        onClick={() => loadPage(page + 1)}
                        disabled={page === totalPages}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Export progress modal */}
      {exporting && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Exporting NCLT Cases</h3>
              <p className="text-sm text-gray-600 mb-6">Please wait...</p>
              <div className="relative mb-6">
                <div className="w-full bg-gray-200 rounded-full h-6">
                  <div
                    className="h-full bg-blue-600 rounded-full transition-all"
                    style={{ width: `${exportProgress.total > 0 ? (exportProgress.exported / exportProgress.total) * 100 : 0}%` }}
                  ></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold">
                    {exportProgress.total > 0
                      ? Math.round((exportProgress.exported / exportProgress.total) * 100)
                      : 0}
                    %
                  </span>
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-sm">Cases Exported:</span>
                  <span className="font-bold">
                    {exportProgress.exported} / {exportProgress.total}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
